/* eslint id-length: 0 */
import { profileHash } from "PFCore/helpers/profile_hash";

import { getTranslationKey } from "../../../helpers/custom_pptx_template";
import { kpmgBlue } from "../constants";
import { getKPMGClientsFieldNames, getKPMGQualificationsFieldNames, isKPMGCA } from "../helpers";

const ptsToInch = (pts) => pts / 72;

const EmptyLine = (data) => {
  data.push({
    text: "",
    options: {
      paraSpaceAfter: 3,
      breakLine: true
    }
  });
};

const CustomField = (data, customTypes, names, title) => {
  const values = (names || []).reduce((acc, name) => {
    const customType = customTypes.find(({ type }) => type.name === name);
    if (customType) {
      const vals = customType.text ? customType.text.split("\r ") : [];
      return [...acc, ...vals];
    }
  }, []);

  if (values.length === 0) {
    return false;
  }

  data.push({
    text: title,
    options: {
      color: kpmgBlue,
      bold: true,
      breakLine: true,
      autoFit: true
    }
  });
  values.map((item) =>
    data.push({
      text: item,
      options: {
        breakLine: true,
        bullet: { indent: 9 }
      }
    })
  );
};

const Positions = (data, positions, title) => {
  if (!positions?.items || positions.items.length === 0) {
    return false;
  }

  data.push({
    text: title,
    options: { paraSpaceAfter: 6, color: kpmgBlue, bold: true, breakLine: true }
  });
  positions.items.forEach(({ summary }) => {
    if (summary) {
      const summaryArray = summary.replace(/\r/g, "").split(/\n/);
      data.push({
        text: summaryArray.shift() || "",
        options: {
          paraSpaceAfter: 6,
          bullet: { indent: 9 }
        }
      });
      summaryArray.forEach((row) =>
        data.push({
          text: row,
          options: {
            softBreakBefore: true
          }
        })
      );
    }
  });
};

const Summary = (data, summary, title) => {
  if (!summary) {
    return false;
  }

  data.push({
    text: title,
    options: {
      paraSpaceAfter: 6,
      color: kpmgBlue,
      bold: true,
      breakLine: true
    }
  });

  const summaryArray = summary.replace(/\r/g, "").split(/\n/);
  summaryArray.forEach((row, index) =>
    data.push({
      text: row,
      options: {
        paraSpaceBefore: 0,
        paraSpaceAfter: 12,
        softBreakBefore: true,
        breakLine: index === summaryArray.length - 1
      }
    })
  );
};

const Contacts = (data, contactInformationItems, email, templateKey) => {
  data.push({
    text: "Contact",
    options: {
      breakLine: true,
      color: kpmgBlue,
      bold: true
    }
  });
  [
    { text: email },
    ...contactInformationItems.filter(({ type }) =>
      isKPMGCA(templateKey) ? /Phone/.test(type) : /mobile_number/.test(type)
    )
  ].map(({ text }) => {
    data.push({
      text,
      options: {
        breakLine: true
      }
    });
  });
};

const localExportBuildKpmgPptx = (data, config, order, pptxgen, t) => {
  const pptx = new pptxgen();

  pptx.defineSlideMaster({
    title: "RESUME",
    background: { color: "FFFFFF" },
    objects: [
      {
        line: {
          x: ptsToInch(190),
          y: ptsToInch(36),
          w: 0,
          h: ptsToInch(457),
          line: { color: kpmgBlue, width: 1 }
        }
      },
      {
        placeholder: {
          options: {
            name: "name",
            type: "title",
            x: ptsToInch(200),
            y: ptsToInch(41),
            w: ptsToInch(460),
            h: ptsToInch(41),
            margin: 0,
            valign: "top",
            align: "left",
            fontFace: "Arial",
            fontSize: 24,
            color: kpmgBlue
          },
          text: ""
        }
      },
      {
        placeholder: {
          options: {
            name: "body",
            type: "body",
            x: ptsToInch(200),
            y: ptsToInch(107),
            w: ptsToInch(460),
            h: ptsToInch(387),
            valign: "top",
            margin: 0,
            fontFace: "Arial",
            fontSize: 9
          },
          text: ""
        }
      }
    ]
  });

  const slide = pptx.addSlide({ masterName: "RESUME" });
  pptx.layout = "LAYOUT_4x3";

  const leftOffset = ptsToInch(59);
  let leftPanelYOffset = ptsToInch(36);

  const { templateKey, isBlindModeOn, hidden } = config;

  const translationKey = getTranslationKey(templateKey);
  const qualificationsFiledNames = getKPMGQualificationsFieldNames(templateKey);
  const clientsFieldNames = getKPMGClientsFieldNames(templateKey);

  // left column (sidebar)
  if (data.avatar && !isBlindModeOn) {
    slide.addImage({
      x: leftOffset,
      y: leftPanelYOffset,
      w: ptsToInch(96),
      h: ptsToInch(113),
      data: data.avatar
    });
  }
  leftPanelYOffset = ptsToInch(168);

  const leftDetailsTextArray = [];

  if (!isBlindModeOn) {
    Contacts(leftDetailsTextArray, data.contactInformationItems, data.email, templateKey);
    EmptyLine(leftDetailsTextArray);
  }

  CustomField(
    leftDetailsTextArray,
    data.customTypes,
    order.filter((itemName) => qualificationsFiledNames.includes(itemName)),
    t(`${translationKey}.certificates`)
  );

  EmptyLine(leftDetailsTextArray);

  CustomField(
    leftDetailsTextArray,
    data.customTypes,
    order.filter((itemName) => clientsFieldNames.includes(itemName)),
    t(`${translationKey}.clients`)
  );

  EmptyLine(leftDetailsTextArray);

  slide.addText(leftDetailsTextArray, {
    x: leftOffset,
    y: leftPanelYOffset,
    w: ptsToInch(119),
    h: ptsToInch(326),
    valign: "top",
    margin: 0,
    fontFace: "Arial",
    fontSize: 8
  });

  // logo
  if (data.logo.custom) {
    slide.addImage({
      x: leftOffset,
      y: ptsToInch(488),
      w: ptsToInch(33),
      h: ptsToInch(33),
      data: data.logo.url
    });
  } else {
    slide.addImage({ x: leftOffset, y: ptsToInch(498), w: 1, h: 0.3, path: data.logo.url });
  }

  // right panel

  slide.addText(isBlindModeOn ? `Candidate ${profileHash(data.id)}` : data.name, {
    placeholder: "name"
  });

  slide.addText(data.jobTitle, {
    x: ptsToInch(200),
    y: ptsToInch(66),
    w: ptsToInch(460),
    h: ptsToInch(17),
    margin: 0,
    valign: "top",
    fontFace: "Arial",
    color: kpmgBlue,
    fontSize: 12,
    bold: true
  });

  const detailsTextArray = [];

  order
    .filter((itemName) => !hidden[itemName])
    .map((itemName) => {
      if (itemName === "summary" && data.summary) {
        Summary(detailsTextArray, data.summary, t(`${translationKey}.summary`));
      }

      if (itemName === "experience") {
        Positions(detailsTextArray, data.positions, t(`${translationKey}.positions`));
      }
    });

  slide.addText(detailsTextArray, { placeholder: "body" });

  return pptx;
};

export default localExportBuildKpmgPptx;
